// src/components/CreateUserModal.tsx
import React, { useState, useEffect } from 'react';
import { 
  Modal, 
  Form, 
  Input, 
  Button, 
  message, 
  Select, 
  Space, 
  Typography,
  Row, 
  Col,
  Tooltip,
  Card,
  Divider,
  Alert,
  Radio
} from 'antd';
import { 
  UserOutlined, 
  LockOutlined, 
  IdcardOutlined, 
  PhoneOutlined, 
  CodeOutlined,
  CopyOutlined,
  CheckCircleOutlined,
  DatabaseOutlined,
  CloudOutlined
} from '@ant-design/icons';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { API_BASE_URL } from '../constants/config';
import { getAndSanitize } from '../shared/utils/sanitization';
import { usePanelsStore } from '../shared/stores/usePanelsStore';

const { Text } = Typography;

interface CreateUserModalProps {
  open: boolean;
  onCancel: () => void;
  onSuccess?: () => void;
}

interface CreateUserFormData {
  username: string;
  accountName: string;
  user_id: string;
  accountId: string;
  phone: string;
  code: string;
  password?: string;
  panelId?: string;
  skipBrowserOperation?: boolean;
}

interface Panel {
  id: string;
  name?: string;
  isActive?: boolean;
}

interface PanelEndpoint {
  id: string;
  name: string;
  baseUrl: string;
  token: string;
  userId: string;
}

interface CreatedUser {
  username: string;
  password: string;
  accountName: string;
  accountId: string;
  user_id: string;
  phone: string;
  code: string;
}

export const CreateUserModal: React.FC<CreateUserModalProps> = ({
  open,
  onCancel,
  onSuccess
}) => {
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  
  // States
  const [availablePanels, setAvailablePanels] = useState<Panel[]>([]);
  const [selectedPanelId, setSelectedPanelId] = useState<string | null>(null);
  const [isLoadingPanels, setIsLoadingPanels] = useState(false);
  const [skipBrowserOperation, setSkipBrowserOperation] = useState(false);
  const [createdUser, setCreatedUser] = useState<CreatedUser | null>(null);
  const [showSuccessState, setShowSuccessState] = useState(false);

  // Fetch panel endpoints for account selectors
  const { data: panelEndpoints } = useQuery({
    queryKey: ['panel-endpoints'],
    queryFn: async () => {
      const token = getAndSanitize('token');
      const response = await axios.get<{ success: boolean; data: PanelEndpoint[] }>(
        `${API_BASE_URL}/api/auth/endpoints`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data.data;
    },
    enabled: open,
  });

  // Load panels when modal opens
  useEffect(() => {
    if (open) {
      loadPanels();
      setShowSuccessState(false);
      setCreatedUser(null);
    }
  }, [open]);

  const loadPanels = async () => {
    try {
      setIsLoadingPanels(true);
      const panels = await usePanelsStore.getState().fetchPanels();
      setAvailablePanels(panels);
      
      if (panels.length === 1) {
        setSelectedPanelId(panels[0].id);
      } else if (panels.length === 0) {
        message.error('No panels available');
      }
    } catch (error) {
      console.error('[CreateUser] Error loading panels:', error);
      message.error('Failed to load panels');
    } finally {
      setIsLoadingPanels(false);
    }
  };

  const createUserMutation = useMutation({
    mutationFn: async (formData: CreateUserFormData) => {
      const token = getAndSanitize('token');
      const adminId = getAndSanitize('userId');
      
      if (!token || !adminId) {
        throw new Error('Authentication required');
      }

      const panelId = formData.panelId || selectedPanelId;
      if (!panelId && !skipBrowserOperation) {
        throw new Error('Panel selection required');
      }

      const response = await axios.post(
        `${API_BASE_URL}/api/panel/operations/users`,
        {
          ...formData,
          adminId,
          panelId,
          skipBrowserOperation
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
            'X-Admin-ID': adminId
          }
        }
      );

      return response.data;
    },
    onSuccess: (data, formData) => {
      message.success(t('users.createUser.success') || 'User created successfully');
      queryClient.invalidateQueries({ queryKey: ['panel-users'] });
      queryClient.invalidateQueries({ queryKey: ['panel-users-select'] });

      // Set created user data and show success state (prefer API response, fallback to form data)
      setCreatedUser({
        username: data.username || formData.username,
        password: data.password || formData.password || '',
        accountName: data.accountName || formData.accountName,
        accountId: data.accountId || formData.accountId || '',
        user_id: data.user_id || formData.user_id,
        phone: data.phone || formData.phone || '',
        code: data.code || formData.code || '',
      });
      setShowSuccessState(true);
      onSuccess?.();
    },
    onError: (error: any) => {
      console.error('[CreateUser] Error:', error);
      message.error(
        error.response?.data?.message || 
        t('users.createUser.error') || 
        'Failed to create user'
      );
    }
  });

  const handleSubmit = (values: CreateUserFormData) => {
    const panelId = selectedPanelId;
    if (!panelId && !skipBrowserOperation) {
      message.error('Please select a panel');
      return;
    }

    if (skipBrowserOperation && !values.password) {
      message.error('Password is required when user already exists');
      return;
    }

    createUserMutation.mutate({
      ...values,
      panelId,
      skipBrowserOperation
    });
  };

  const handleCancel = () => {
    form.resetFields();
    setSelectedPanelId(null);
    setSkipBrowserOperation(false);
    setShowSuccessState(false);
    setCreatedUser(null);
    onCancel();
  };

const handleCopyCredentials = async (username: string, password?: string) => {
  try {
    const token = getAndSanitize('token');
    let format = "Usuario: {username}\nClave: {password}";
    
    // Extract adminId from JWT token
    let adminId: string | null = null;
    if (token) {
      try {
        const decodedToken = JSON.parse(atob(token.split('.')[1]));
        adminId = decodedToken?.userId;
      } catch (decodeError) {
        console.warn('Error decoding token for adminId:', decodeError);
      }
    }
    
    // Try to get format from settings with adminId
    if (adminId) {
      try {
        const settingsResponse = await axios.get(
          `${API_BASE_URL}/api/settings`,
          { 
            headers: { Authorization: `Bearer ${token}` },
            params: { adminId }
          }
        );
        
        if (settingsResponse?.data?.success && settingsResponse?.data?.data?.credentialFormat) {
          format = settingsResponse.data.data.credentialFormat;
        } else if (settingsResponse?.data?.credentialFormat) {
          format = settingsResponse.data.credentialFormat;
        }
      } catch (error) {
        console.warn('Error getting credential format from settings, using default:', error);
      }
    }
    
    if (password) {
      // Handle both {username}/{password} placeholders and literal username/password text
      const formattedCredential = format
        // Handle proper placeholders first
        .replace(/{username}/g, username)
        .replace(/{password}/g, password)
        // Handle literal text as fallback
        .replace(/\busername\b/g, username)
        .replace(/\bpassword\b/g, password)
        // Convert escaped newlines to actual newlines
        .replace(/\\\\n/g, '\n')
        .replace(/\\n/g, '\n');
        
      await navigator.clipboard.writeText(formattedCredential);
      message.success('Credentials copied to clipboard');
      return;
    }
    
    // If we don't have the password, request it from the server
    const response = await axios.post(
      `${API_BASE_URL}/api/panel/user/credentials`,
      { username },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    
    if (response.data.success) {
      const formattedCredential = format
        // Handle proper placeholders first
        .replace(/{username}/g, response.data.data.username)
        .replace(/{password}/g, response.data.data.password)
        // Handle literal text as fallback
        .replace(/\busername\b/g, response.data.data.username)
        .replace(/\bpassword\b/g, response.data.data.password)
        // Convert escaped newlines to actual newlines
        .replace(/\\\\n/g, '\n')
        .replace(/\\n/g, '\n');
        
      await navigator.clipboard.writeText(formattedCredential);
      message.success('Credentials copied to clipboard');
    }
  } catch (error) {
    console.error("Error copying credentials:", error);
    message.error('Error copying credentials');
  }
};


  const createNewUser = () => {
    form.resetFields();
    setSelectedPanelId(null);
    setSkipBrowserOperation(false);
    setShowSuccessState(false);
    setCreatedUser(null);
  };

  // Handle account name change to auto-select account ID
  const handleAccountNameChange = (accountName: string) => {
    const selectedEndpoint = panelEndpoints?.find(endpoint => endpoint.name === accountName);
    if (selectedEndpoint) {
      form.setFieldValue('accountId', selectedEndpoint.id);
    }
  };

  // Get unique account names from panel endpoints
  const accountOptions = panelEndpoints?.map(endpoint => ({
    label: endpoint.name,
    value: endpoint.name,
    id: endpoint.id
  })) || [];

  return (
    <Modal
      title={
        showSuccessState 
          ? <Space><CheckCircleOutlined style={{ color: '#52c41a' }} />User Created Successfully</Space>
          : t('users.createUser.title') || 'Create New User'
      }
      open={open}
      onCancel={handleCancel}
      footer={null}
      destroyOnClose
      width={800}
    >
      {showSuccessState && createdUser ? (
        // Success State
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <Alert
            message="User created successfully!"
            description="The user has been created and is ready to use. You can copy the credentials or create another user."
            type="success"
            showIcon
          />
          
          <Card title="User Information">
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Text strong>Username:</Text>
                  <Text copyable={{ text: createdUser.username }}>
                    {createdUser.username}
                  </Text>
                </Space>
              </Col>
              <Col span={12}>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Text strong>Password:</Text>
                  <Text copyable={{ text: createdUser.password }}>
                    {createdUser.password || '-'}
                  </Text>
                </Space>
              </Col>
              <Col span={12}>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Text strong>Account Name:</Text>
                  <Text copyable={{ text: createdUser.accountName }}>
                    {createdUser.accountName}
                  </Text>
                </Space>
              </Col>
              <Col span={12}>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Text strong>Account ID:</Text>
                  <Text copyable={{ text: createdUser.accountId }}>
                    {createdUser.accountId || '-'}
                  </Text>
                </Space>
              </Col>
              {createdUser.user_id && (
                <Col span={12}>
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Text strong>User ID:</Text>
                    <Text copyable={{ text: createdUser.user_id }}>
                      {createdUser.user_id}
                    </Text>
                  </Space>
                </Col>
              )}
              {createdUser.phone && (
                <Col span={12}>
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Text strong>Phone:</Text>
                    <Text copyable={{ text: createdUser.phone }}>
                      {createdUser.phone}
                    </Text>
                  </Space>
                </Col>
              )}
              {createdUser.code && (
                <Col span={12}>
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Text strong>Tracking Code:</Text>
                    <Text copyable={{ text: createdUser.code }}>
                      {createdUser.code}
                    </Text>
                  </Space>
                </Col>
              )}
            </Row>
          </Card>

          <Row gutter={16}>
            <Col span={12}>
              <Button 
                type="primary" 
                icon={<CopyOutlined />} 
                onClick={() => handleCopyCredentials(createdUser.username, createdUser.password)}
                size="large"
                block
                style={{ height: '48px' }}
              >
                Copy Full Credentials
              </Button>
            </Col>
            <Col span={12}>
              <Button 
                onClick={createNewUser}
                size="large"
                block
                style={{ height: '48px' }}
              >
                Create Another User
              </Button>
            </Col>
          </Row>
        </Space>
      ) : (
        // Form State
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          {/* Operation Mode Selection */}
          <Card 
            size="small" 
            style={{ 
              marginBottom: 24, 
              background: '#fafafa',
              border: '1px solid #d9d9d9',
              borderRadius: '6px'
            }}
          >
            <Form.Item style={{ marginBottom: 0 }}>
              <Radio.Group 
                value={skipBrowserOperation} 
                onChange={(e) => setSkipBrowserOperation(e.target.value)}
              >
                <Space direction="vertical">
                  <Radio value={false}>
                    <Space>
                      <CloudOutlined style={{ color: '#1890ff' }} />
                      <span>Create new user in platform</span>
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        (Creates user in both platform and database)
                      </Text>
                    </Space>
                  </Radio>
                  <Radio value={true}>
                    <Space>
                      <DatabaseOutlined style={{ color: '#52c41a' }} />
                      <span>Database only</span>
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        (Save exact username and password to database without platform creation)
                      </Text>
                    </Space>
                  </Radio>
                </Space>
              </Radio.Group>
            </Form.Item>
          </Card>

          {/* Panel Selection */}
          {!skipBrowserOperation && availablePanels.length > 1 && (
            <Card 
              size="small" 
              title="Panel Selection"
              style={{ 
                marginBottom: 24,
                border: '1px solid #d9d9d9',
                borderRadius: '6px'
              }}
            >
              <Form.Item style={{ marginBottom: 0 }}>
                <Select
                  placeholder="Select a panel for user creation"
                  value={selectedPanelId}
                  onChange={setSelectedPanelId}
                  loading={isLoadingPanels}
                  size="large"
                  options={availablePanels.map(panel => ({
                    value: panel.id,
                    label: (
                      <Space>
                        <Text>{panel.name || panel.id}</Text>
                        {panel.isActive && (
                          <Text style={{ color: '#52c41a', fontSize: '12px' }}>
                            (Active)
                          </Text>
                        )}
                      </Space>
                    )
                  }))}
                />
              </Form.Item>
            </Card>
          )}

          {!skipBrowserOperation && availablePanels.length === 1 && (
            <Card 
              size="small" 
              style={{ 
                marginBottom: 24,
                background: '#f6ffed',
                border: '1px solid #b7eb8f'
              }}
            >
              <Space>
                <Text strong>Selected Panel:</Text>
                <Text>{availablePanels[0]?.name || availablePanels[0]?.id}</Text>
              </Space>
            </Card>
          )}

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="username"
                label={
                  <Tooltip title="The exact username as it appears in the platform. Must match exactly, including capitalization.">
                    <Space><UserOutlined />Username <span style={{ color: '#ff4d4f' }}>*</span></Space>
                  </Tooltip>
                }
                rules={[
                  { required: true, message: 'Username is required' },
                  { min: 3, message: 'Username must be at least 3 characters' }
                ]}
              >
                <Input 
                  placeholder="Enter exact platform username"
                  prefix={<UserOutlined />}
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              {skipBrowserOperation && (
                <Form.Item
                  name="password"
                  label={
                    <Tooltip title="The platform password for this user">
                      <Space><LockOutlined />Password <span style={{ color: '#ff4d4f' }}>*</span></Space>
                    </Tooltip>
                  }
                  rules={[
                    { required: true, message: 'Password is required' }
                  ]}
                >
                  <Input.Password 
                    placeholder="Enter platform password" 
                    prefix={<LockOutlined />}
                  />
                </Form.Item>
              )}
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="accountName"
                label={
                  <Tooltip title="Select the channel/account name where the deposit comes from">
                    <Space>Account Name <span style={{ color: '#ff4d4f' }}>*</span></Space>
                  </Tooltip>
                }
                rules={[
                  { required: true, message: 'Account name is required' }
                ]}
              >
                <Select
                  placeholder="Select account name"
                  showSearch
                  onChange={handleAccountNameChange}
                  filterOption={(input, option) =>
                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  options={accountOptions}
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name="accountId"
                label={
                  <Tooltip title="Account ID will be automatically set based on selected account name">
                    <Space>Account ID <span style={{ color: '#ff4d4f' }}>*</span></Space>
                  </Tooltip>
                }
                rules={[
                  { required: true, message: 'Account ID is required' }
                ]}
              >
                <Input
                  placeholder="Auto-selected based on account name"
                  disabled
                  style={{ color: '#000', background: '#f5f5f5' }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="user_id"
                label={
                  <Tooltip title="User ID, could be the phone number or any unique identifier">
                    <Space><IdcardOutlined />User ID</Space>
                  </Tooltip>
                }
              >
                <Input 
                  placeholder="e.g., phone number or unique ID"
                  prefix={<IdcardOutlined />}
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name="phone"
                label={
                  <Tooltip title="Client's phone number with country and area codes. Example for Buenos Aires, Argentina: 5411XXXXXXXX">
                    <Space><PhoneOutlined />Phone</Space>
                  </Tooltip>
                }
              >
                <Input 
                  placeholder="5411XXXXXXXX (Buenos Aires, Argentina)"
                  prefix={<PhoneOutlined />}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="code"
                label={
                  <Tooltip title="Code from the client's first message to track the deposit">
                    <Space><CodeOutlined />Tracking Code</Space>
                  </Tooltip>
                }
              >
                <Input 
                  placeholder="Enter code from client's first message"
                  prefix={<CodeOutlined />}
                />
              </Form.Item>
            </Col>
          </Row>

          <Divider />

          <Form.Item style={{ marginBottom: 0 }}>
            <Row gutter={16}>
              <Col span={12}>
                <Button onClick={handleCancel} size="large" block>
                  Cancel
                </Button>
              </Col>
              <Col span={12}>
                <Button 
                  type="primary" 
                  htmlType="submit" 
                  loading={createUserMutation.isPending}
                  disabled={!skipBrowserOperation && availablePanels.length > 1 && !selectedPanelId}
                  size="large"
                  block
                >
                  Create User
                </Button>
              </Col>
            </Row>
          </Form.Item>
        </Form>
      )}
    </Modal>
  );
};